/**
 * Vercel API client - handles all requests to local Vercel functions
 */
import { useQuery } from "@tanstack/react-query";
import logger from "../utils/logger";
interface ChatContext {
  track?: string;
  race?: string | number;
  date?: string;
  hasHistoricalData?: boolean;
  analysis?: any;
}

/**
 * Handles 401 authentication errors by clearing session and redirecting to login
 * @param {Response} response - The fetch response object
 * @returns {boolean} - Returns true if 401 was handled, false otherwise
 */
const handle401Error = (response: Response): boolean => {
  if (response.status === 401) {
    logger.error("[handle401Error] Authentication failed (401)");
    logger.error("[handle401Error] Token state before clearing:", {
      hasSessionToken: !!sessionStorage.getItem("authToken"),
      hasLocalToken: !!localStorage.getItem("authToken"),
      hasSessionSid: !!sessionStorage.getItem("verificationSid"),
      hasLocalSid: !!localStorage.getItem("verificationSid")
    });
    logger.warn(
      "Authentication failed (401) - clearing auth tokens and redirecting to login",
    );

    // Clear only auth-related storage; keep verificationSid to avoid re-verification churn
    if (typeof window !== "undefined") {
      sessionStorage.removeItem("authToken");
      // sessionStorage.removeItem("userEmail");
      // sessionStorage.removeItem("phoneNumber");
      // Keep verificationSid so flows that rely on it are not unnecessarily disrupted

      // Local storage: drop auth/session data but preserve verificationSid
      localStorage.removeItem("sessionData");
      localStorage.removeItem("authToken");
      // localStorage.removeItem("userEmail");
      // localStorage.removeItem("phoneNumber");
      // Intentionally do NOT remove localStorage verificationSid here

      // Redirect to login page
      window.location.href = "/login";
    }

    return true;
  }
  return false;
};

/**
 * Enhanced fetch wrapper that handles 401 errors automatically
 * @param {string} url - The URL to fetch
 * @param {RequestInit} options - Fetch options
 * @returns {Promise<Response>} - The fetch response
 */
const fetchWithAuth = async (
  url: string,
  options: RequestInit = {},
): Promise<Response> => {
  const authToken = getAuthToken();

  logger.log(`[fetchWithAuth] Making request to ${url}`, {
    hasToken: !!authToken,
    tokenLength: authToken?.length || 0
  });

  // Add Authorization header if we have a token
  if (authToken) {
    const headers = options.headers || {};
    if (typeof headers === 'object' && !('Authorization' in headers)) {
      options.headers = {
        ...headers,
        Authorization: `Bearer ${authToken}`,
      };
      logger.log('[fetchWithAuth] Added Authorization header');
    }
  } else {
    logger.warn('[fetchWithAuth] No auth token available for request');
  }

  const response = await fetch(url, options);

  logger.log(`[fetchWithAuth] Response from ${url}:`, {
    status: response.status,
    ok: response.ok
  });

  // Handle 401 errors automatically
  if (handle401Error(response)) {
    throw new Error("Authentication failed - redirecting to login");
  }

  return response;
};

interface AbandonedMeetingResponse {
  status: string;
  data: {
    status: string;
    data: Array<{
      id: string;
      fullMeetingName: string;
      trackName: string;
      State: string;
      reason: string;
      meetingDate: string;
      date: string;
      lastUpdated: {
        _seconds: number;
        _nanoseconds: number;
      };
    }>;
  };
  message?: string;
}

interface HistoricalResultsResponse {
  status: string;
  data: Record<string, any>;
  message?: string;
}

interface LeaderboardOptions {
  date?: string;
  collection?: string;
}

interface HistoricalResultsOptions {
  date?: string;
  fetchAll?: boolean;
}

interface ChatResponse {
  role: string;
  content: string;
}

/**
 * Send a chat message to the AI
 * @param {string} message - The user's message
 * @param {string} userId - The user's ID (phone number)
 * @param {ChatContext} context - Context containing date, track, race info
 * @returns {Promise<ChatResponse>} - Assistant's response
 */
export async function sendChatMessage(
  message: string,
  userId: string,
  context?: ChatContext,
): Promise<ChatResponse> {
  try {
    const userMessage = { content: message };

    // Format track name by removing spaces and making it consistent
    const formattedTrack = context?.track
      ? context.track.replace(/\s+/g, "-").toLowerCase()
      : "";
    const raceNumber = context?.race || "";
    const contextMarker =
      formattedTrack && raceNumber
        ? `${formattedTrack}-race-${raceNumber}`
        : "";

    // Create request body
    const requestBody = {
      userId: userId || "anonymous-user",
      message: userMessage.content,
      date: context?.date,
      hasHistoricalData: context?.hasHistoricalData,
      analysis: context?.analysis,
      track: context?.track,
      race: context?.race,
      contextMarker,
    };

    // Get auth token for authorization header
    const authToken = getAuthToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    // Call the Vercel API function
    const response = await fetchWithAuth("/api/chat", {
      method: "POST",
      headers,
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorText = await response.text();
      logger.error("API Error:", {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
      });
      throw new Error(
        `API Error: ${response.status} - ${errorText || response.statusText}`,
      );
    }

    return await response.json();
  } catch (error) {
    logger.error("Error in sendChatMessage:", error);
    throw error;
  }
}

/**
 * Fetch chat history for a specific user and track/race combination
 * @param {string} userId - The user's ID (phone number)
 * @param {ChatContext} context - Context containing track and race information
 * @returns {Promise<Array<ChatResponse>>} - Array of chat messages
 */
export async function fetchChatHistory(
  userId: string,
  context?: ChatContext,
): Promise<Array<ChatResponse>> {
  try {
    if (!userId) {
      logger.error("User ID is required to fetch chat history");
      return [];
    }

    // Format track name by removing spaces and making it consistent
    const formattedTrack = context?.track
      ? context.track.replace(/\s+/g, "-").toLowerCase()
      : "";
    const raceNumber = context?.race || "";

    if (!formattedTrack || !raceNumber) {
      logger.warn(
        "Track or race not provided, cannot fetch specific chat history",
      );
      return [];
    }

    // Create the session ID in the exact format seen in Firestore
    // Format: +61411619993-2025-04-17-albury-race-1
    const today = new Date();
    const formattedDate = `${today.getFullYear()}-${String(
      today.getMonth() + 1,
    ).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

    const sessionId = `${userId}-${formattedDate}-${formattedTrack}-race-${raceNumber}`;

    // Get auth token for authorization header
    const authToken = getAuthToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    // Call the Vercel API function
    const response = await fetchWithAuth("/api/chat-history", {
      method: "POST",
      headers,
      body: JSON.stringify({
        userId,
        sessionId,
      }),
    });

    if (!response.ok) {
      logger.error(
        "Failed to fetch chat history:",
        response.status,
        response.statusText,
      );
      return [];
    }

    return await response.json();
  } catch (error) {
    logger.error("Error fetching chat history:", error);
    return [];
  }
}

/**
 * Fetches leaderboard data from the API
 * @param {LeaderboardOptions} options - Optional parameters for the request
 * @returns {Promise<any>}
 */
export const fetchLeaderboardData = async (
  options: LeaderboardOptions = {},
): Promise<any> => {
  const { date, collection = "predictions" } = options;

  // Get auth token for authorization header
  const authToken = getAuthToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }

  const response = await fetchWithAuth("/api/leaderboard", {
    method: "POST",
    headers,
    body: JSON.stringify({
      date,
      collection,
    }),
  });

  return response.json();
};

// ============================================================================
// FUTURE TIPS (RACES FOR DATE)
// ============================================================================

/**
 * Fetch "future tips" / races for a given date (DD-MM-YYYY) via Vercel proxy.
 */
export const fetchFutureRacesForDate = async (date: string): Promise<any> => {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  const authToken = getAuthToken();
  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }

  const response = await fetchWithAuth("/api/future-tips", {
    method: "POST",
    headers,
    body: JSON.stringify({ date }),
  });

  return response.json();
};

/**
 * React Query hook for fetching "future tips" for a given date (DD-MM-YYYY).
 */
export const useFutureTipsQuery = (options: any = {}) => {
  const {
    date,
    staleTime = 30 * 60 * 1000, // 30 minutes
    gcTime = 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus = false,
    refetchOnMount = false,
    enabled = true,
  } = options;

  return useQuery({
    queryKey: ["future-tips", date],
    queryFn: () => fetchFutureRacesForDate(date),
    staleTime,
    gcTime,
    refetchOnWindowFocus,
    refetchOnMount,
    enabled: enabled && !!date,
  });
};

/**
 * React Query hook for fetching and managing leaderboard data
 * Direct replacement for useLeaderboardQuery from @/api/leaderboard
 * @param {Object} options - Optional parameters for the request
 */
export const useLeaderboardQuery = (options: any = {}) => {
  const {
    date,
    collection = "predictions",
    staleTime = 5 * 60 * 1000, // 5 minutes
    gcTime = 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus = false,
    refetchOnMount = false,
    enabled = true,
  } = options;

  return useQuery({
    queryKey: ["leaderboard", date, collection],
    queryFn: () => fetchLeaderboardData({ date, collection }),
    staleTime,
    gcTime,
    refetchOnWindowFocus,
    refetchOnMount,
    refetchInterval: 60000, // 1 minute
    enabled,
  });
};

/**
 * Fetches leaderboard data with JWT authentication instead of auth token
 * Copy of fetchLeaderboardData but uses generateJWT() for authorization
 * @param {LeaderboardOptions} options - Options for fetching leaderboard data
 * @returns {Promise<any>} - Leaderboard data response
 */
export const fetchLeaderboardDataWithJWT = async (
  options: LeaderboardOptions = {},
): Promise<any> => {
  const { date, collection = "predictions" } = options;

  // Generate JWT for authorization header
  const jwtToken = await generateJWT();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (jwtToken) {
    headers.Authorization = `Bearer ${jwtToken}`;
  }

  const response = await fetch("/api/leaderboard", {
    method: "POST",
    headers,
    body: JSON.stringify({
      date,
      collection,
    }),
  });

  return response.json();
};

/**
 * React Query hook for fetching and managing leaderboard data with JWT authentication
 * Copy of useLeaderboardQuery but uses fetchLeaderboardDataWithJWT
 * @param {Object} options - Optional parameters for the request
 */
export const useLeaderboardJWTQuery = (options: any = {}) => {
  const {
    date,
    collection = "predictions",
    staleTime = 5 * 60 * 1000, // 5 minutes
    gcTime = 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus = false,
    refetchOnMount = false,
    enabled = true,
  } = options;

  return useQuery({
    queryKey: ["leaderboard-jwt", date, collection],
    queryFn: () => fetchLeaderboardDataWithJWT({ date, collection }),
    staleTime,
    gcTime,
    refetchOnWindowFocus,
    refetchOnMount,
    refetchInterval: 60000, // 1 minute
    enabled,
  });
};

/**
 * React Query hook for fetching early predictions
 * Direct replacement for useEarlyPredictionsQuery from @/api/leaderboard
 * @param {Object} options - Optional parameters for the request
 */
export const useEarlyPredictionsQuery = (options: any = {}) => {
  const {
    date,
    staleTime = 10 * 60 * 1000, // 10 minutes
    gcTime = 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus = false,
    refetchOnMount = false,
    selectedTrack = null,
    selectedRace = null,
    enabled = true,
  } = options;

  return useQuery({
    queryKey: ["early-predictions", date],
    queryFn: () =>
      fetchLeaderboardData({ date, collection: "early-predictions" }),
    staleTime,
    gcTime,
    refetchOnWindowFocus,
    refetchOnMount,
    refetchInterval: (query) => {
      const data = query.state.data;

      // If no data yet, keep refetching every minute
      if (!data?.data) return 60000;

      // If no selected track or race, keep refetching
      if (!selectedTrack || !selectedRace) return 60000;

      // Get the race key for the current race
      const raceKey = `${selectedTrack.replace(/\s+/g, "-")}-${selectedRace}-${date}`;

      // Get early predictions data for this race
      const earlyData = data.data[raceKey];

      // If no early data, keep refetching
      if (!earlyData) return 60000;

      // Check if we have valid early predictions
      const hasFirstFourData =
        earlyData.firstFourData &&
        Array.isArray(earlyData.firstFourData) &&
        earlyData.firstFourData.length > 0;

      // If no firstFourData, keep refetching
      if (!hasFirstFourData) return 60000;

      // Additional check: if tipUpdatedAt exists and has a valid value
      const hasValidTipUpdatedAt = !!earlyData.tipUpdatedAt;

      // If we have early predictions with valid data, stop refetching
      return hasFirstFourData && hasValidTipUpdatedAt ? false : 60000;
    },
    enabled,
  });
};

/**
 * Fetches data about abandoned meetings
 * @param {string} date - Date in DD-MM-YYYY format
 * @returns {Promise<AbandonedMeetingResponse>} - Response with abandoned meetings data
 */
export const fetchAbandonedMeetings = async (
  date: string,
): Promise<AbandonedMeetingResponse> => {
  try {
    if (!date) {
      logger.error("Date is required to fetch abandoned meetings");
      return {
        status: "error",
        message: "Date is required",
        data: { status: "error", data: [] },
      };
    }

    // Get auth token for authorization header
    const authToken = getAuthToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const response = await fetchWithAuth("/api/abandoned-meetings", {
      method: "POST",
      headers,
      body: JSON.stringify({ date }),
    });

    if (!response.ok) {
      logger.error(
        "Failed to fetch abandoned meetings:",
        response.status,
        response.statusText,
      );
      return {
        status: "error",
        message: "Failed to fetch data",
        data: { status: "error", data: [] },
      };
    }

    return await response.json();
  } catch (error) {
    logger.error("Error fetching abandoned meetings:", error);
    const errorMessage = error instanceof Error ? error.message : String(error);
    return {
      status: "error",
      message: errorMessage,
      data: { status: "error", data: [] },
    };
  }
};

/**
 * Fetches historical results from the API
 * @param {HistoricalResultsOptions} options - Options for the request
 * @returns {Promise<HistoricalResultsResponse>} - Response with historical results
 */
export const fetchHistoricalResults = async (
  options: HistoricalResultsOptions = {},
): Promise<HistoricalResultsResponse> => {
  try {
    const { date, fetchAll = false } = options;

    // Require either date or fetchAll
    if (!fetchAll && !date) {
      logger.error("Either date or fetchAll is required");
      return {
        status: "error",
        message: "Either date or fetchAll parameter is required",
        data: {},
      };
    }

    // Get auth token for authorization header
    const authToken = getAuthToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const response = await fetchWithAuth("/api/historical-results", {
      method: "POST",
      headers,
      body: JSON.stringify({ date, fetchAll }),
    });

    if (!response.ok) {
      logger.error(
        "Failed to fetch historical results:",
        response.status,
        response.statusText,
      );
      return {
        status: "error",
        message: "Failed to fetch historical results",
        data: {},
      };
    }

    return await response.json();
  } catch (error) {
    logger.error("Error fetching historical results:", error);
    const errorMessage = error instanceof Error ? error.message : String(error);
    return {
      status: "error",
      message: errorMessage,
      data: {},
    };
  }
};

/**
 * React Query hook for fetching historical results
 * Direct replacement for useHistoricalResultsQuery from @/api/leaderboard
 * @param {Object} options - Options for the query
 */
export const useHistoricalResultsQuery = (options: any = {}) => {
  const {
    date,
    fetchAll = false,
    staleTime = 10 * 60 * 1000, // 10 minutes
    gcTime = 60 * 60 * 1000, // 60 minutes
    refetchOnWindowFocus = false,
    refetchOnMount = false,
    enabled = true,
  } = options;

  return useQuery({
    queryKey: ["historical-results", date, fetchAll],
    queryFn: () => fetchHistoricalResults({ date, fetchAll }),
    staleTime,
    gcTime,
    refetchOnWindowFocus,
    refetchOnMount,
    refetchInterval: 60000, // 1 minute
    enabled: enabled && (fetchAll || !!date), // Enable if fetchAll is true or if date is provided
  });
};

/**
 * Fetches one day's race cards (prize money, Group, class, race name, distance)
 * keyed by normalised track name. Optional extra: errors return empty data and
 * never log the user out (plain fetch, not fetchWithAuth).
 * @param {Object} options
 * @param {string} options.date - "dd-MM-yyyy" or "YYYY-MM-DD"
 */
export const fetchRaceCards = async ({ date }: { date?: string } = {}) => {
  try {
    const authToken = getAuthToken();
    const response = await fetch("/api/race-cards", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
      body: JSON.stringify({ date }),
    });
    if (!response.ok) {
      logger.warn("Failed to fetch race cards:", response.status);
      return { status: "error", data: {} };
    }
    return await response.json();
  } catch (error) {
    logger.warn("Error fetching race cards:", error);
    return { status: "error", data: {} };
  }
};

/**
 * React Query hook for one day's race cards (see fetchRaceCards)
 * @param {Object} options - { date, enabled }
 */
export const useRaceCardsQuery = (options: any = {}) => {
  const { date, enabled = true } = options;
  return useQuery({
    queryKey: ["race-cards", date],
    queryFn: () => fetchRaceCards({ date }),
    staleTime: 30 * 60 * 1000, // cards change once a day
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: enabled && !!date,
  });
};

/**
 * Fetches historical results with JWT authentication instead of auth token
 * Copy of fetchHistoricalResults but uses generateJWT() for authorization
 * @param {HistoricalResultsOptions} options - Options for fetching historical results
 * @returns {Promise<HistoricalResultsResponse>} - Historical results response
 */
export const fetchHistoricalResultsWithJWT = async (
  options: HistoricalResultsOptions = {},
): Promise<HistoricalResultsResponse> => {
  try {
    const { date, fetchAll = false } = options;

    // Require either date or fetchAll
    if (!fetchAll && !date) {
      logger.error("Either date or fetchAll is required");
      return {
        status: "error",
        message: "Either date or fetchAll parameter is required",
        data: {},
      };
    }

    // Generate JWT for authorization header
    const jwtToken = await generateJWT();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (jwtToken) {
      headers.Authorization = `Bearer ${jwtToken}`;
    }

    const response = await fetch("/api/historical-results", {
      method: "POST",
      headers,
      body: JSON.stringify({ date, fetchAll }),
    });

    if (!response.ok) {
      logger.error(
        "Failed to fetch historical results:",
        response.status,
        response.statusText,
      );
      return {
        status: "error",
        message: "Failed to fetch historical results",
        data: {},
      };
    }

    return await response.json();
  } catch (error) {
    logger.error("Error fetching historical results:", error);
    const errorMessage = error instanceof Error ? error.message : String(error);
    return {
      status: "error",
      message: errorMessage,
      data: {},
    };
  }
};

/**
 * React Query hook for fetching historical results with JWT authentication
 * Copy of useHistoricalResultsQuery but uses fetchHistoricalResultsWithJWT
 * @param {Object} options - Options for the query
 */
export const useHistoricalResultsJWTQuery = (options: any = {}) => {
  const {
    date,
    fetchAll = false,
    staleTime = 10 * 60 * 1000, // 10 minutes
    gcTime = 60 * 60 * 1000, // 60 minutes
    refetchOnWindowFocus = false,
    refetchOnMount = false,
    enabled = true,
  } = options;

  return useQuery({
    queryKey: ["historical-results-jwt", date, fetchAll],
    queryFn: () => fetchHistoricalResultsWithJWT({ date, fetchAll }),
    staleTime,
    gcTime,
    refetchOnWindowFocus,
    refetchOnMount,
    refetchInterval: 60000, // 1 minute
    enabled: enabled && (fetchAll || !!date), // Enable if fetchAll is true or if date is provided
  });
};

/**
 * Sends a verification code to a phone number
 * @param {string} email - User's email address
 * @param {string} phoneNumber - User's phone number
 * @returns {Promise<any>} - Response with verification status
 */
export const sendVerificationCode = async (
  email: string,
  phoneNumber: string,
): Promise<any> => {
  try {
    const response = await fetchWithAuth("/api/send-verification", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email,
        phoneNumber,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to send verification code");
    }

    return await response.json();
  } catch (error) {
    logger.error("Error sending verification code:", error);
    throw error;
  }
};

/**
 * Verifies a JWT token
 * @param {string} token - The JWT token to verify
 * @returns {Promise<any>} - Response with verification result
 */
export const verifyJWT = async (token: string): Promise<any> => {
  try {
    if (!token) {
      throw new Error("Token is required");
    }

    const response = await fetchWithAuth("/api/verify-jwt", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "JWT verification failed");
    }

    return await response.json();
  } catch (error) {
    logger.error("Error verifying JWT:", error);
    throw error;
  }
};

/**
 * Helper function to get the auth token from session storage
 * @returns {string|null} - The auth token or null if not found
 */
export const getAuthToken = (): string | null => {
  if (typeof window !== "undefined") {
    // Check for preview_token in URL
    try {
      const url = new URL(window.location.href);
      const previewToken = url.searchParams.get("preview_token");
      if (previewToken) {
        logger.log('[getAuthToken] Using preview token from URL');
        return previewToken;
      }
    } catch (e) {
      // ignore
    }
    // Prefer sessionStorage, but fall back to localStorage
    const sessionToken = sessionStorage.getItem("authToken");
    const localToken = localStorage.getItem("authToken");
    
    const token = sessionToken || localToken;
    logger.log('[getAuthToken] Token check:', {
      hasSessionToken: !!sessionToken,
      hasLocalToken: !!localToken,
      usingToken: token ? 'found' : 'none',
      tokenLength: token?.length || 0
    });
    
    return token;
  }
  return null;
};

/**
 * Helper function to generate a JWT using the generate-jwt API
 * @returns {Promise<string|null>} - The generated JWT or null if failed
 */
export const generateJWT = async (): Promise<string | null> => {
  try {
    const response = await fetch("/api/generate-jwt", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      logger.error("Failed to generate JWT:", response.status, response.statusText);
      return null;
    }

    const data = await response.json();
    return data.token || null;
  } catch (error) {
    logger.error("Error generating JWT:", error);
    return null;
  }
};

/**
 * Verifies a code sent to the user's phone
 * @param {string} phoneNumber - User's phone number
 * @param {string} email - User's email
 * @param {string} code - Verification code
 * @returns {Promise<any>} - Response with verification result
 */
export const verifyCode = async (
  phoneNumber: string,
  email: string,
  code: string,
): Promise<any> => {
  try {
    const response = await fetchWithAuth("/api/verify-code", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        phoneNumber,
        email,
        code,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to verify code");
    }

    return await response.json();
  } catch (error) {
    logger.error("Error verifying code:", error);
    throw error;
  }
};

/**
 * Takes a screenshot of a specific tip
 * @param {Object} options - Options for the screenshot
 * @param {string} options.trackName - Name of the track
 * @param {string|number} options.raceNumber - Race number
 * @returns {Promise<{success: boolean, screenshot: string}>} - The screenshot URL
 */
export const takeScreenshot = async ({
  trackName,
  raceNumber,
}: {
  trackName: string;
  raceNumber: string | number;
}): Promise<{ success: boolean; screenshot: string }> => {
  try {
    const response = await fetchWithAuth("/api/screenshot", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        trackName,
        raceNumber,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      logger.error("Screenshot API Error:", {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
      });
      throw new Error(
        `Screenshot API Error: ${response.status} - ${errorText || response.statusText}`
      );
    }

    return await response.json();
  } catch (error) {
    logger.error("Error taking screenshot:", error);
    throw error;
  }
};

/**
 * Takes a screenshot of the home page
 * @returns {Promise<{success: boolean, screenshot: string}>} - The screenshot URL
 */
export const takeHomeScreenshot = async (token: string): Promise<{ success: boolean; screenshot: string }> => {
  try {
    const response = await fetchWithAuth("/api/home-screenshot", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      const errorText = await response.text();
      logger.error("Home Screenshot API Error:", {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
      });
      throw new Error(
        `Home Screenshot API Error: ${response.status} - ${errorText || response.statusText}`
      );
    }

    return await response.json();
  } catch (error) {
    logger.error("Error taking home screenshot:", error);
    throw error;
  }
};

/**
 * Takes a screenshot of the daily wrap page
 * @param {Object} options - Screenshot options
 * @param {string} options.wins - Number of wins to display
 * @param {string} options.places - Number of places to display
 * @param {string} options.screenshotUrl - URL of screenshot to display in the wrap
 * @returns {Promise<{success: boolean, screenshot: string}>} - The screenshot URL
 */
export const takeDailyWrapScreenshot = async ({
  wins,
  places,
  screenshotUrl
}: {
  wins?: string;
  places?: string;
  screenshotUrl?: string;
}): Promise<{ success: boolean; screenshot: string }> => {
  try {
    const response = await fetchWithAuth("/api/screenshot-daily-wrap", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ wins, places, screenshotUrl }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      logger.error("Daily Wrap Screenshot API Error:", {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
      });
      throw new Error(
        `Daily Wrap Screenshot API Error: ${response.status} - ${errorText || response.statusText}`
      );
    }

    return await response.json();
  } catch (error) {
    logger.error("Error taking daily wrap screenshot:", error);
    throw error;
  }
};

interface WinningTip {
  date: string;
  trackName: string;
  raceNumber: string;
  horseNumber: string;
  horseName: string;
  odds: string;
  raceDate: Date;
  key: string;
}

interface WinningTipsResponse {
  status: string;
  data: {
    winningTips: WinningTip[];
    totalFound: number;
    daysSearched: number;
    searchStartDate: string;
    searchEndDate: string;
  };
  message: string;
}

interface WinningTipsOptions {
  minWinners?: number;
}

/**
 * Fetches winning tips starting from yesterday and going back until we have at least the minimum number of winners
 * @param {WinningTipsOptions} options - Options for the request
 * @param {number} options.minWinners - Minimum number of winners to fetch (default: 10)
 * @returns {Promise<WinningTipsResponse>} - Response with winning tips data
 */
export const fetchWinningTips = async (
  options: WinningTipsOptions = {},
): Promise<WinningTipsResponse> => {
  try {
    const { minWinners = 10 } = options;

    // Get auth token for authorization header
    const authToken = getAuthToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const response = await fetchWithAuth("/api/winning-tips", {
      method: "POST",
      headers,
      body: JSON.stringify({ minWinners }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      logger.error("Winning Tips API Error:", {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
      });
      throw new Error(
        `Winning Tips API Error: ${response.status} - ${errorText || response.statusText}`
      );
    }

    return await response.json();
  } catch (error) {
    logger.error("Error fetching winning tips:", error);
    throw error;
  }
};

/**
 * React Query hook for fetching winning tips
 * @param {Object} options - Options for the query
 * @param {number} options.minWinners - Minimum number of winners to fetch (default: 10)
 * @param {number} options.staleTime - How long data is considered fresh (default: 5 minutes)
 * @param {number} options.gcTime - How long to keep unused data in cache (default: 30 minutes)
 * @param {boolean} options.refetchOnWindowFocus - Whether to refetch when window regains focus (default: false)
 * @param {boolean} options.refetchOnMount - Whether to refetch on component mount (default: false)
 * @param {boolean} options.enabled - Whether the query should execute (default: true)
 */
export const useWinningTipsQuery = (options: any = {}) => {
  const {
    minWinners = 10,
    staleTime = 5 * 60 * 1000, // 5 minutes
    gcTime = 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus = false,
    refetchOnMount = false,
    enabled = true,
  } = options;

  return useQuery({
    queryKey: ["winning-tips", minWinners],
    queryFn: () => fetchWinningTips({ minWinners }),
    staleTime,
    gcTime,
    refetchOnWindowFocus,
    refetchOnMount,
    refetchInterval: 5 * 60 * 1000, // 5 minutes
    enabled,
  });
};

// ============================================================================
// TRACK PERFORMANCE API FUNCTIONS
// ============================================================================

interface TrackPerformanceResponse {
  success: boolean;
  message: string;
  data: Array<{
    id: string;
    trackCode: string;
    totalRaces: number;
    lastUpdated: any;
    models: Record<string, any> | null;
  }>;
  count: number;
  filters: {
    trackNames: string[] | null;
    limit: number;
  };
  database: string;
  collection: string;
}

interface TrackPerformanceOptions {
  trackNames?: string[];
  limit?: number;
}

/**
 * Fetches track performance data from the API
 * @param {TrackPerformanceOptions} options - Options for the request
 * @returns {Promise<TrackPerformanceResponse>} - Response with track performance data
 */
export const fetchTrackPerformance = async (
  options: TrackPerformanceOptions = {},
): Promise<TrackPerformanceResponse> => {
  try {
    const { trackNames, limit = 100 } = options;

    // // Get auth token for authorization header
    // const authToken = getAuthToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    // if (authToken) {
    //   headers.Authorization = `Bearer ${authToken}`;
    // }

    const response = await fetch("/api/read-track-performance", {
      method: "POST",
      headers,
      body: JSON.stringify({ trackNames, limit }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      logger.error("Track Performance API Error:", {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
      });
      throw new Error(
        `Track Performance API Error: ${response.status} - ${errorText || response.statusText}`
      );
    }

    return await response.json();
  } catch (error) {
    logger.error("Error fetching track performance:", error);
    throw error;
  }
};

/**
 * React Query hook for fetching track performance data
 * @param {Object} options - Options for the query
 * @param {string[]} options.trackNames - Array of track names to filter by
 * @param {number} options.limit - Maximum number of records to return (default: 100)
 * @param {number} options.staleTime - How long data is considered fresh (default: 10 minutes)
 * @param {number} options.gcTime - How long to keep unused data in cache (default: 30 minutes)
 * @param {boolean} options.refetchOnWindowFocus - Whether to refetch when window regains focus (default: false)
 * @param {boolean} options.refetchOnMount - Whether to refetch on component mount (default: false)
 * @param {boolean} options.enabled - Whether the query should execute (default: true)
 */
export const useTrackPerformanceQuery = (options: any = {}) => {
  const {
    trackNames,
    limit = 100,
    staleTime = 10 * 60 * 1000, // 10 minutes
    gcTime = 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus = false,
    refetchOnMount = false,
    enabled = true,
  } = options;

  return useQuery({
    queryKey: ["track-performance", trackNames, limit],
    queryFn: () => fetchTrackPerformance({ trackNames, limit }),
    staleTime,
    gcTime,
    refetchOnWindowFocus,
    refetchOnMount,
    enabled,
  });
};

// ============================================================================
// TRACK PERFORMANCE MEDIANS API FUNCTIONS
// ============================================================================

interface TrackPerformanceMediansResponse {
  success: boolean;
  message: string;
  data: Array<{
    id: string;
    trackCode: string;
    totalRaces: number;
    lastUpdated: any;
    models: Record<string, any> | null;
  }>;
  count: number;
  filters: {
    trackNames: string[] | null;
    limit: number;
  };
  database: string;
  collection: string;
}

interface TrackPerformanceMediansOptions {
  trackNames?: string[];
  limit?: number;
}

/**
 * Fetches track performance medians data from the API
 * @param {TrackPerformanceMediansOptions} options - Options for the request
 * @returns {Promise<TrackPerformanceMediansResponse>} - Response with track performance medians data
 */
export const fetchTrackPerformanceMedians = async (
  options: TrackPerformanceMediansOptions = {},
): Promise<TrackPerformanceMediansResponse> => {
  try {
    const { trackNames, limit = 100 } = options;

    // Get auth token for authorization header
    // const authToken = getAuthToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    // if (authToken) {
    //   headers.Authorization = `Bearer ${authToken}`;
    // }

    const response = await fetch("/api/read-track-performance-medians", {
      method: "POST",
      headers,
      body: JSON.stringify({ trackNames, limit }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      logger.error("Track Performance Medians API Error:", {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
      });
      throw new Error(
        `Track Performance Medians API Error: ${response.status} - ${errorText || response.statusText}`
      );
    }

    return await response.json();
  } catch (error) {
    logger.error("Error fetching track performance medians:", error);
    throw error;
  }
};

/**
 * React Query hook for fetching track performance medians data
 * @param {Object} options - Options for the query
 * @param {string[]} options.trackNames - Array of track names to filter by
 * @param {number} options.limit - Maximum number of records to return (default: 100)
 * @param {number} options.staleTime - How long data is considered fresh (default: 10 minutes)
 * @param {number} options.gcTime - How long to keep unused data in cache (default: 30 minutes)
 * @param {boolean} options.refetchOnWindowFocus - Whether to refetch when window regains focus (default: false)
 * @param {boolean} options.refetchOnMount - Whether to refetch on component mount (default: false)
 * @param {boolean} options.enabled - Whether the query should execute (default: true)
 */
export const useTrackPerformanceMediansQuery = (options: any = {}) => {
  const {
    trackNames,
    limit = 200,
    staleTime = 10 * 60 * 1000, // 10 minutes
    gcTime = 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus = false,
    refetchOnMount = false,
    enabled = true,
  } = options;

  return useQuery({
    queryKey: ["track-performance-medians", trackNames, limit],
    queryFn: () => fetchTrackPerformanceMedians({ trackNames, limit }),
    staleTime,
    gcTime,
    refetchOnWindowFocus,
    refetchOnMount,
    enabled,
  });
};

// ============================================================================
// MODEL SUMMARY STATS API FUNCTIONS
// ============================================================================

interface ModelSummaryStatsResponse {
  success: boolean;
  message: string;
  data: Array<{
    id: string;
    dateKey: string;
    early: Record<string, any> | null;
    lastUpdated?: any;
  }>;
  count: number;
  filters: {
    modelNames: string[] | null;
    limit: number;
  };
  database: string;
  collection: string;
}

interface ModelSummaryStatsOptions {
  modelNames?: string[];
  limit?: number;
}

/**
 * Fetches model summary stats data from the API
 * @param {ModelSummaryStatsOptions} options - Options for the request
 * @returns {Promise<ModelSummaryStatsResponse>} - Response with model summary stats data
 */
export const fetchModelSummaryStats = async (
  options: ModelSummaryStatsOptions = {},
): Promise<ModelSummaryStatsResponse> => {
  try {
    const { modelNames, limit = 200 } = options;

    // Get auth token for authorization header
    // const authToken = getAuthToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    // if (authToken) {
    //   headers.Authorization = `Bearer ${authToken}`;
    // }

    const response = await fetch("/api/read-model-summary-stats", {
      method: "POST",
      headers,
      body: JSON.stringify({ modelNames, limit }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      logger.error("Model Summary Stats API Error:", {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
      });
      throw new Error(
        `Model Summary Stats API Error: ${response.status} - ${errorText || response.statusText}`
      );
    }

    return await response.json();
  } catch (error) {
    logger.error("Error fetching model summary stats:", error);
    throw error;
  }
};

/**
 * React Query hook for fetching model summary stats data
 * @param {Object} options - Options for the query
 * @param {string[]} options.modelNames - Array of model names to filter by
 * @param {number} options.limit - Maximum number of records to return (default: 100)
 * @param {number} options.staleTime - How long data is considered fresh (default: 10 minutes)
 * @param {number} options.gcTime - How long to keep unused data in cache (default: 30 minutes)
 * @param {boolean} options.refetchOnWindowFocus - Whether to refetch when window regains focus (default: false)
 * @param {boolean} options.refetchOnMount - Whether to refetch on component mount (default: false)
 * @param {boolean} options.enabled - Whether the query should execute (default: true)
 */
export const useModelSummaryStatsQuery = (options: any = {}) => {
  const {
    modelNames,
    limit = 200,
    staleTime = 10 * 60 * 1000, // 10 minutes
    gcTime = 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus = false,
    refetchOnMount = false,
    enabled = true,
  } = options;

  return useQuery({
    queryKey: ["model-summary-stats", modelNames, limit],
    queryFn: () => fetchModelSummaryStats({ modelNames, limit }),
    staleTime,
    gcTime,
    refetchOnWindowFocus,
    refetchOnMount,
    enabled,
  });
};

// ============================================================================
// RESULTS ARCHIVE FILTER API FUNCTIONS
// ============================================================================

interface ResultsArchiveResponse {
  status: string;
  message: string;
  data: Array<{
    id: string;
    modelId: string;
    predictionData?: {
      firstFourData?: Array<{
        formattedName: string;
        selectionId: number;
        currentPrice?: number;
        lastPriceTraded?: number;
      }>;
      race?: number;
      marketStartTime?: string;
      tipUpdatedAt?: string;
    };
    earlyPredictionData?: {
      firstFourData?: Array<{
        formattedName: string;
        selectionId: number;
        currentPrice?: number;
        lastPriceTraded?: number;
      }>;
      race?: number;
      marketStartTime?: string;
      tipUpdatedAt?: string;
    };
    betfairResults?: {
      numberOfRunners?: number;
      result?: Array<{
        selectionId: number;
        status: string;
        placePayout?: number;
        winPayout?: number;
      }>;
    };
    podium?: string[];
    earlyPodium?: string[];
    date?: string;
    meetingName?: string;
    archivedAt?: any;
  }>;
  count: number;
  filters: {
    trackName: string | null;
    date: string | null;
  };
  timestamp: string;
}

interface ResultsArchiveOptions {
  action: "get_results" | "get_tracks" | "get_dates";
  trackName?: string;
  date?: string;
}

/**
 * Fetches results archive data from the API
 * @param {ResultsArchiveOptions} options - Options for the request
 * @returns {Promise<ResultsArchiveResponse>} - Response with results archive data
 */
export const fetchResultsArchive = async (
  options: ResultsArchiveOptions
): Promise<ResultsArchiveResponse> => {
  try {
    const { action, trackName, date } = options;

    // Get auth token for authorization header
    const authToken = getAuthToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const response = await fetchWithAuth("/api/results-archive-filter", {
      method: "POST",
      headers,
      body: JSON.stringify({ action, trackName, date }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      logger.error("Results Archive API Error:", {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
      });
      throw new Error(
        `Results Archive API Error: ${response.status} - ${errorText || response.statusText}`
      );
    }

    return await response.json();
  } catch (error) {
    logger.error("Error fetching results archive:", error);
    throw error;
  }
};

/**
 * React Query hook for fetching results archive data
 * @param {Object} options - Options for the query
 * @param {string} options.action - Action to perform ("get_results", "get_tracks", "get_dates")
 * @param {string} options.trackName - Track name to filter by (optional)
 * @param {string} options.date - Date to filter by (optional)
 * @param {number} options.staleTime - How long data is considered fresh (default: 10 minutes)
 * @param {number} options.gcTime - How long to keep unused data in cache (default: 30 minutes)
 * @param {boolean} options.refetchOnWindowFocus - Whether to refetch when window regains focus (default: false)
 * @param {boolean} options.refetchOnMount - Whether to refetch on component mount (default: false)
 * @param {boolean} options.enabled - Whether the query should execute (default: true)
 */
export const useResultsArchiveQuery = (options: any = {}) => {
  const {
    action = "get_results",
    trackName,
    date,
    staleTime = 10 * 60 * 1000, // 10 minutes
    gcTime = 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus = false,
    refetchOnMount = false,
    enabled = true,
  } = options;

  return useQuery({
    queryKey: ["results-archive", action, trackName, date],
    queryFn: () => fetchResultsArchive({ action, trackName, date }),
    staleTime,
    gcTime,
    refetchOnWindowFocus,
    refetchOnMount,
    enabled: enabled && !!action,
  });
};

// ============================================================================
// URL MAPPER API
// ============================================================================

interface UrlMapItem {
  id: string;
  urlInput: string;
  urlDestination: string;
  createdAt?: string;
  updatedAt?: string;
}

interface UrlMapperResponse {
  success: boolean;
  message?: string;
  data?: UrlMapItem[];
  count?: number;
  error?: string;
}

/**
 * Calls the url-mapper API with the given payload
 */
export const callUrlMapper = async (payload: any): Promise<UrlMapperResponse> => {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const response = await fetchWithAuth("/api/url-mapper", {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });
  return response.json();
};

/**
 * React Query hook: read all URL mappings
 */
export const useUrlMappingsQuery = (options: any = {}) => {
  const { enabled = true, staleTime = 5 * 60 * 1000, gcTime = 30 * 60 * 1000 } = options;

  return useQuery({
    queryKey: ["url-mappings", "read"],
    queryFn: async () => {
      const result = await callUrlMapper({ action: "read" });
      if (!result?.success) {
        throw new Error(result?.error || "Failed to read url mappings");
      }
      return result;
    },
    staleTime,
    gcTime,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled,
  });
};

// ============================================================================
// STRIPE PRICES API
// ============================================================================

interface StripePrice {
  id: string;
  active: boolean;
  currency: string;
  unitAmount: number | null;
  type: string;
  recurring: {
    interval: string;
    intervalCount: number;
    usageType: string;
    trialPeriodDays: number | null;
  } | null;
  nickname: string | null;
  metadata: Record<string, any>;
}

interface StripeProduct {
  id: string;
  active: boolean;
  name: string;
  description: string | null;
  images: string[];
  metadata: Record<string, any>;
  prices: StripePrice[];
}

interface StripePricesResponse {
  success: boolean;
  environment: string;
  count: number;
  hasMore: boolean;
  products: StripeProduct[];
}

/**
 * Fetches active Stripe prices from the API
 * @returns {Promise<StripePricesResponse>} - Response with Stripe products and prices
 */
export const fetchStripePrices = async (): Promise<StripePricesResponse> => {
  try {
    const response = await fetch("/api/stripe-prices", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      logger.error("Stripe Prices API Error:", {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
      });
      throw new Error(
        `Stripe Prices API Error: ${response.status} - ${errorText || response.statusText}`,
      );
    }

    return await response.json();
  } catch (error) {
    logger.error("Error fetching Stripe prices:", error);
    throw error;
  }
};

/**
 * React Query hook for fetching Stripe prices
 * @param {Object} options - Options for the query
 */
export const useStripePricesQuery = (options: any = {}) => {
  const {
    staleTime = 10 * 60 * 1000, // 10 minutes
    gcTime = 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus = false,
    refetchOnMount = false,
    enabled = true,
  } = options;

  return useQuery({
    queryKey: ["stripe-prices"],
    queryFn: fetchStripePrices,
    staleTime,
    gcTime,
    refetchOnWindowFocus,
    refetchOnMount,
    enabled,
  });
};

// ============================================================================
// CREATE PROSPECT API FUNCTIONS
// ============================================================================

interface CreateProspectRequest {
  email: string;
  mobile: string;
  track: string;
  username?: string;
}

interface CreateProspectResponse {
  success: boolean;
  userId?: string;
  prospectId?: string;
  signupAt?: string;
  conversionDueAt?: string;
  requestId?: string;
  error?: string;
  message?: string;
}

/**
 * Creates a prospect user (freemium signup)
 * @param {CreateProspectRequest} data - Prospect data
 * @returns {Promise<CreateProspectResponse>} - Response with prospect creation result
 */
export const createProspect = async (
  data: CreateProspectRequest,
): Promise<CreateProspectResponse> => {
  try {
    const { email, mobile, track, username } = data;

    if (!email || !mobile || !track) {
      throw new Error("Email, mobile, and track are required");
    }

    const response = await fetch("/api/create-prospect", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email: email.toLowerCase(),
        mobile: mobile.replace(/\s/g, ""),
        track: track.toLowerCase().replace(/\s+/g, "-"),
        username,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to create prospect");
    }

    return await response.json();
  } catch (error) {
    logger.error("Error creating prospect:", error);
    throw error;
  }
};

// ============================================================================
// STRIPE CUSTOMER API FUNCTIONS
// ============================================================================

interface StripeCustomerRequest {
  action: string;
  firebaseId?: string;
  onboarding?: boolean;
  [key: string]: any;
}

interface StripeCustomerResponse {
  success: boolean;
  message?: string;
  error?: string;
  [key: string]: any;
}

/**
 * Calls the Stripe Customer API with dynamic action
 * @param {StripeCustomerRequest} data - Request data with action and other params
 * @returns {Promise<StripeCustomerResponse>} - Response from the API
 */
export const callStripeCustomerAPI = async (
  data: StripeCustomerRequest,
): Promise<StripeCustomerResponse> => {
  try {
    const { action } = data;

    if (!action) {
      throw new Error("Action is required");
    }

    const response = await fetchWithAuth("/api/stripe-customer", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Stripe customer API request failed");
    }

    return await response.json();
  } catch (error) {
    logger.error("Error calling Stripe customer API:", error);
    throw error;
  }
};

/**
 * Updates the onboarding status for a customer
 * @param {string} firebaseId - The Firebase customer ID
 * @param {boolean} onboarding - The onboarding status
 * @returns {Promise<StripeCustomerResponse>} - Response from the API
 */
export const updateOnboardingStatus = async (
  firebaseId: string,
  onboarding: boolean,
): Promise<StripeCustomerResponse> => {
  return callStripeCustomerAPI({
    action: "update-onboarding",
    firebaseId,
    onboarding,
  });
};

/**
 * Updates the stripe customer id for a customer
 * @param {string} firebaseId - The Firebase customer ID
 * @param {string} stripeCustomerId - The Stripe customer ID
 * @returns {Promise<StripeCustomerResponse>} - Response from the API
 */
export const linkStripeCustomerId = async (
  firebaseId: string,
  stripeCustomerId: string,
): Promise<StripeCustomerResponse> => {
  return callStripeCustomerAPI({
    action: "link",
    firebaseId,
    stripeCustomerId,
  });
};

// ============================================================================
// SEND WELCOME EMAIL API FUNCTIONS
// ============================================================================

interface SendWelcomeEmailRequest {
  email: string;
  firstName?: string;
}

interface SendWelcomeEmailResponse {
  success: boolean;
  message?: string;
  error?: string;
}

/**
 * Sends a welcome email to a new user
 * @param {SendWelcomeEmailRequest} data - Email data
 * @returns {Promise<SendWelcomeEmailResponse>} - Response from the API
 */
export const sendWelcomeEmail = async (
  data: SendWelcomeEmailRequest,
): Promise<SendWelcomeEmailResponse> => {
  try {
    const { email, firstName } = data;

    if (!email) {
      throw new Error("Email is required");
    }

    const response = await fetch("/api/send-welcome-email", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email: email.toLowerCase(),
        firstName,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to send welcome email");
    }

    return await response.json();
  } catch (error) {
    logger.error("Error sending welcome email:", error);
    throw error;
  }
};
